import { useT } from '@transifex/react'
import { useEffect } from 'react'
import { trackCustomHeapEvent } from '@vend/utilities'
import { FulfilmentDetail } from '../../../../../models/fulfilment'
import {
  HEAP_ADDRESS_NOT_VALIDATED_OBJECT,
  HEAP_ADDRESS_VALIDATED_OBJECT,
  HEAP_DELIVERY_SALE_ACTION,
  HEAP_NO_ADDRESS_OBJECT,
} from '../../../sale-model.constant'
import {
  AddressErrors,
  AddressErrorsStatus,
  useFulfillmentHelper,
} from '../popover/hooks/useFulfillmentHelper'

export interface LineItemFulfilmentAddressProps {
  fulfillmentDetail: FulfilmentDetail | undefined
  isModalOpeningOnError?: boolean
  lineItemLocalId?: string
  onSave?: (fulfillmentDetails: FulfilmentDetail[]) => void
}

export const useLineItemFulfilmentAddressModel = ({
  fulfillmentDetail,
  isModalOpeningOnError,
}: LineItemFulfilmentAddressProps) => {
  const t = useT()
  const { getLineItemFulfillmentAddressDetails } = useFulfillmentHelper()

  const {
    isSameBillingAddress,
    formattedShippingAddress,
    formattedBillingAddress,
    isAddressEmpty,
    error,
    status,
  } = getLineItemFulfillmentAddressDetails(fulfillmentDetail)

  const shouldShowError =
    !!error &&
    (!isModalOpeningOnError ||
      status !== AddressErrorsStatus.EMPTYSHIPPING_CORRECTBILLING)

  const noShippingAddress = !fulfillmentDetail || isAddressEmpty

  const hasBillingAddressInModal =
    isModalOpeningOnError &&
    (status === AddressErrorsStatus.EMPTYSHIPPING_CORRECTBILLING ||
      status === AddressErrorsStatus.EMPTYSHIPPING_INCORRECTBILLING)

  const showEmptyAddressButton = noShippingAddress && !hasBillingAddressInModal

  useEffect(() => {
    const objectName = showEmptyAddressButton
      ? HEAP_NO_ADDRESS_OBJECT
      : status === AddressErrorsStatus.CORRECTSHIPPING_CORRECTBILLING
      ? HEAP_ADDRESS_VALIDATED_OBJECT
      : HEAP_ADDRESS_NOT_VALIDATED_OBJECT

    trackCustomHeapEvent({
      action: HEAP_DELIVERY_SALE_ACTION,
      object: objectName,
    })
    // intentionally omit dependencies to track this event only once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const errorMessage =
    error === AddressErrors.ADD_ADDRESS
      ? t('Add shipping and billing address to complete the sale', {
          _context: 'Alert text',
        })
      : error === AddressErrors.ADDRESS_INCORRECT
      ? t('Update incorrect address details to complete the sale', {
          _context: 'Alert text',
        })
      : error === AddressErrors.ADDRESS_REQUIRED
      ? t('Shipping & billing address required for fulfillment', {
          _context: 'Alert text',
        })
      : undefined

  return {
    error,
    errorMessage,
    formattedBillingAddress,
    formattedShippingAddress,
    isSameBillingAddress,
    shouldShowError,
    showEmptyAddressButton,
    status,
    autotaxCountryId: fulfillmentDetail?.shippingAddress?.country_id,
  }
}
